/**
 * AiChatPanel — the main AsterAI / ChainAI chat UI.
 *
 * Layout: chat transcript at top, evidence cards inline under each AI turn,
 * suggested-question chips and an input at the bottom. Phase 1 is read-only
 * so we don't render any "take action" affordances.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Send, Sparkles, AlertCircle, Loader2 } from 'lucide-react';

import AiEvidenceCard from './AiEvidenceCard';
import { askAi, fetchSuggestions, AIProduct } from '@/api/aiApi';

const productLabel = (p) =>
  p === AIProduct.CHAIN29 ? 'ChainAI' : 'AsterAI';

export default function AiChatPanel({ product = AIProduct.CHAIN29 }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchSuggestions(product)
      .then((s) => { if (!cancelled) setSuggestions(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [product]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setError(null);
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setInput('');
    setLoading(true);
    try {
      const resp = await askAi({ query: q, product });
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text: resp?.answer || '',
          evidence: resp?.evidence_cards || [],
          followups: resp?.suggested_followups || [],
          blocked: resp?.blocked_reason || null,
          latency: resp?.latency_ms,
        },
      ]);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setError('Please sign in to use ' + productLabel(product) + '.');
      } else if (status === 403) {
        setError(
          e?.response?.data?.detail ||
            "Your role doesn't have access to this assistant.",
        );
      } else {
        setError('The AI service is temporarily unavailable.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    send();
  };

  return (
    <div
      data-testid="ai-chat-panel"
      className="flex h-full min-h-[520px] flex-col rounded-2xl border border-zinc-200 bg-white shadow-sm"
    >
      <header className="flex items-center gap-2 border-b border-zinc-100 px-5 py-3">
        <Sparkles className="h-5 w-5 text-amber-500" />
        <div className="text-lg font-semibold tracking-tight">
          {productLabel(product)}
        </div>
        <div className="ml-auto text-xs text-zinc-400">
          Answers questions · changes nothing
        </div>
      </header>

      <div
        ref={scrollerRef}
        className="flex-1 space-y-4 overflow-y-auto px-5 py-4"
        data-testid="ai-chat-transcript"
      >
        {messages.length === 0 && !loading ? (
          <div className="text-sm text-zinc-500">
            Ask anything about your business. Examples below.
          </div>
        ) : null}

        {messages.map((m, idx) => (
          <div key={idx} className="space-y-3">
            {m.role === 'user' ? (
              <div
                data-testid="ai-message-user"
                className="ml-auto w-fit max-w-[80%] rounded-2xl bg-zinc-900 px-4 py-2 text-sm text-white"
              >
                {m.text}
              </div>
            ) : (
              <div className="space-y-2">
                <div
                  data-testid="ai-message-assistant"
                  className="w-fit max-w-[80%] whitespace-pre-wrap rounded-2xl bg-amber-50 px-4 py-2 text-sm text-zinc-900"
                >
                  {m.text}
                </div>
                {m.evidence && m.evidence.length > 0 ? (
                  <div
                    data-testid="ai-evidence-list"
                    className="grid gap-2 sm:grid-cols-2"
                  >
                    {m.evidence.map((c, i) => (
                      <AiEvidenceCard key={i} card={c} />
                    ))}
                  </div>
                ) : null}
                {m.followups && m.followups.length > 0 ? (
                  <div className="flex flex-wrap gap-2 pt-1" data-testid="ai-followups">
                    {m.followups.slice(0, 3).map((f, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => send(f)}
                        className="rounded-full border border-amber-300 bg-amber-100/60 px-3 py-1 text-xs text-amber-900 transition hover:bg-amber-100"
                        data-testid="ai-followup-btn"
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className="text-[10px] text-zinc-400">
                  {typeof m.latency === 'number' ? `${m.latency} ms` : null}
                </div>
              </div>
            )}
          </div>
        ))}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500" data-testid="ai-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            Thinking…
          </div>
        ) : null}

        {error ? (
          <div
            className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
            data-testid="ai-error"
          >
            <AlertCircle className="mt-0.5 h-4 w-4" />
            <div>{error}</div>
          </div>
        ) : null}
      </div>

      {suggestions.length > 0 && messages.length === 0 ? (
        <div className="border-t border-zinc-100 px-5 py-3" data-testid="ai-suggestions">
          <div className="mb-2 text-xs uppercase tracking-wide text-zinc-400">
            Try asking
          </div>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => send(s)}
                className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs text-zinc-700 transition hover:bg-zinc-100"
                data-testid="ai-suggestion-chip"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 border-t border-zinc-100 px-5 py-3"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Ask ${productLabel(product)} a question…`}
          className="flex-1 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-400"
          disabled={loading}
          data-testid="ai-chat-input"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="inline-flex items-center gap-1 rounded-xl bg-amber-500 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-zinc-300"
          data-testid="ai-chat-send"
        >
          <Send className="h-4 w-4" />
          Ask
        </button>
      </form>
    </div>
  );
}
